import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useHaptics } from '../../utils/haptics';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { titleCase } from '../../utils/accountApi';

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

export default function AddressBook() {
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const settings = usePublicSettings();
  const defaultCity = titleCase(settings?.['market.default_city']);
  const defaultCountry = settings?.['market.country_name'] || '';

  // Form state
  const [formData, setFormData] = useState({
    label: 'Home',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    latitude: null,
    longitude: null,
    is_default: false
  });

  // Country defaults to the market's country (setting market.country_name)
  useEffect(() => {
    if (defaultCountry) setFormData(prev => (prev.country ? prev : { ...prev, country: defaultCountry }));
  }, [defaultCountry, showAddForm]);

  // Fetch addresses on mount
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/customers/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      showError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  // Detect GPS location
  const detectLocation = () => {
    if (!navigator.geolocation) {
      showError('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Update form with coordinates
        setFormData(prev => ({
          ...prev,
          latitude,
          longitude
        }));

        // Reverse geocode to get address
        try {
          const response = await axios.post(`${API_BASE_URL}/api/customers/reverse-geocode`, {
            latitude,
            longitude
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          const { city, state, country } = response.data;
          setFormData(prev => ({
            ...prev,
            city: city || prev.city,
            state_province: state || prev.state_province,
            country: country || prev.country
          }));

          showSuccess(`Location detected: ${city}, ${country}`);
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          showSuccess(`GPS coordinates detected: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        }

        setDetectingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        showError('Failed to detect location. Please enable location services.');
        setDetectingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Save address
  const saveAddress = async (e) => {
    e.preventDefault();

    if (!formData.address_line_1 || !formData.city || !formData.country) {
      showError('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/customers/addresses`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAddresses([...addresses, response.data]);
      showSuccess('Address saved successfully!');
      
      // Reset form
      setFormData({
        label: 'Home',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: '',
        latitude: null,
        longitude: null,
        is_default: false
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving address:', error);
      showError('Failed to save address');
    }
  };

  // Set default address
  const setDefaultAddress = async (addressId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/customers/addresses/${addressId}/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setAddresses(addresses.map(addr => ({
        ...addr,
        is_default: addr.id === addressId
      })));

      showSuccess('Default address updated');
    } catch (error) {
      console.error('Error setting default address:', error);
      showError('Failed to update default address');
    }
  };

  // Delete address
  const deleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/api/customers/addresses/${addressId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAddresses(addresses.filter(addr => addr.id !== addressId));
      showSuccess('Address deleted');
    } catch (error) {
      console.error('Error deleting address:', error);
      showError('Failed to delete address');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading addresses...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center gap-3 mb-6">
        <button 
          onClick={() => {
            haptics.light()
            navigate(-1)
          }}
          className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
        >
          <ArrowLeftIcon />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 flex-1">Address Book</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Address'}
        </button>
      </div>

      {/* Add Address Form */}
      {showAddForm && (
        <form onSubmit={saveAddress} className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Address</h2>

          {/* GPS Detection Button */}
          <button
            type="button"
            onClick={detectLocation}
            disabled={detectingLocation}
            className="w-full bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 hover:bg-green-100 transition-colors flex items-center justify-center gap-2"
          >
            {detectingLocation ? (
              <>
                <div className="w-5 h-5 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                Detecting location...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                📍 Use My Current Location
              </>
            )}
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
              <select
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="Home">Home</option>
                <option value="Office">Office</option>
                <option value="Vacation Home">Vacation Home</option>
                <option value="Rental Property">Rental Property</option>
                <option value="Parent's House">Parent's House</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder={defaultCountry}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Address Line 1 */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
              <input
                type="text"
                value={formData.address_line_1}
                onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                placeholder="123 Main Street"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Address Line 2 */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Apartment, Suite, etc. (Optional)</label>
              <input
                type="text"
                value={formData.address_line_2}
                onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                placeholder="Apt 4B"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder={defaultCity}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* State/Province */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <input
                type="text"
                value={formData.state_province}
                onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
                placeholder="State / province"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder="1234"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Default Checkbox */}
            <div className="md:col-span-2 flex items-center">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_default" className="ml-2 text-sm text-gray-700">
                Set as default address
              </label>
            </div>
          </div>

          {/* GPS Coordinates Display */}
          {formData.latitude && formData.longitude && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm text-green-800">
              📍 GPS Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Address
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Address List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {addresses.length === 0 ? (
          <div className="md:col-span-2 text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-600 mb-4">No addresses saved yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first address
            </button>
          </div>
        ) : (
          addresses.map((address) => (
            <div
              key={address.id}
              className={`bg-white rounded-xl shadow-md p-4 border-2 ${
                address.is_default ? 'border-blue-500' : 'border-transparent'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{address.label}</h3>
                  {address.is_default && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                <button
                  onClick={() => deleteAddress(address.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-700 mb-1">{address.address_line_1}</p>
              {address.address_line_2 && (
                <p className="text-sm text-gray-700 mb-1">{address.address_line_2}</p>
              )}
              <p className="text-sm text-gray-700">
                {address.city}, {address.state_province && `${address.state_province}, `}{address.country}
              </p>
              {address.postal_code && (
                <p className="text-sm text-gray-600 mt-1">{address.postal_code}</p>
              )}

              {!address.is_default && (
                <button
                  onClick={() => setDefaultAddress(address.id)}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Set as default
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

